import type { ConfigKey } from "src/services/authService";

/** Key of a backend extension toggle in Platform Configuration. */
export type ExtensionKey = `extension_${string}`;

/**
 * The extensions the UI gates on. Route and menu gating name the same one, so
 * they cannot drift apart the way two bare string literals could.
 */
export const EXTENSION_FLAGS = {
  attributeLimits: "extension_attribute_limits",
  attributeValueHistory: "extension_attribute_value_history",
  inventory: "extension_inventory",
  inventoryGroup: "extension_inventory_group",
  inventoryLimits: "extension_inventory_limits",
  inventoryLocation: "extension_inventory_location",
  locationHistory: "extension_location_history",
  locationSequence: "extension_location_sequence",
  sensorLocationRestrictions: "extension_sensor_location_restrictions",
} as const satisfies Record<string, ExtensionKey>;

/**
 * Whether a backend extension is enabled, given the configuration rows. A plain
 * function rather than a hook, so route and menu gating can read keys that only
 * exist at runtime; `useExtensionFlag` wraps it for components.
 */
export const isExtensionEnabled = (
  configKeys: ConfigKey[] | undefined,
  key: ExtensionKey,
): boolean => {
  // No configuration read at all, so no licence to read either. Nothing gated
  // is shown; `AppLayout` surfaces the failure rather than leaving the app
  // quietly half empty.
  if (!configKeys) {
    return false;
  }

  const row = configKeys.find((configKey) => configKey.key === key);

  // Only a bool carries "1"/"0". Anything else means the key was renamed or
  // retyped behind us, and reading its value as a flag would be a guess.
  if (row?.value_type === "bool") {
    return row.value === "1";
  }

  // A key this backend does not serve: the UI is allowed to ship ahead of the
  // backend, so the feature stays visible.
  if (import.meta.env.DEV) {
    console.warn(
      `Config key "${key}" ${row ? `is ${row.value_type}, not bool` : "is missing"}; treating as enabled.`,
    );
  }

  return true;
};

interface RouteExtensionHandle {
  configFlag?: ExtensionKey;
}

const isRouteExtensionHandle = (
  handle: unknown,
): handle is RouteExtensionHandle =>
  !!handle && typeof handle === "object" && "configFlag" in handle;

/** The extension a route belongs to, where it belongs to one. */
export const getRouteConfigFlag = (
  handle: unknown,
): ExtensionKey | undefined =>
  isRouteExtensionHandle(handle) ? handle.configFlag : undefined;
